// DiePreview.jsx — shared SVG outline of a detected die polygon.
// vertices are mm, Y-up, CCW, local origin (the SourceResult contract shape).
//
// Two modes:
//
//   <DiePreview vertices={v} />                        self-scaling (default)
//     Normalises to the polygon's own bounding box. Every shape fills the
//     frame, which is right when shapes are shown independently — a library
//     thumbnail, a detection result.
//
//   <DiePreview vertices={v} viewBox={[0,0,W,H]} />    shared scale (PR ⑭b)
//     Draws into a caller-supplied frame WITHOUT renormalising, so two
//     polygons given the same viewBox render at the same mm-per-pixel and can
//     honestly be compared. Vertices are already anchored at their own local
//     origin, so both shapes align bottom-left — the truthful common frame,
//     and the only registration we can claim (rev 6 §5.2).
import React from 'react';
import { useTheme } from './theme';

export default function DiePreview({ vertices, size = 116, P: pIn, viewBox = null }) {
  const theme = useTheme();
  const P = pIn || theme.P;
  if (!Array.isArray(vertices) || vertices.length < 3) return null;

  const shared = Array.isArray(viewBox) && viewBox.length === 4 && viewBox.every(Number.isFinite);

  if (shared) {
    const [, , W, H] = viewBox;
    if (!(W > 0) || !(H > 0)) return null;
    // Flip Y for SVG (Y-down) about the SHARED height, not the polygon's own —
    // that is what keeps the scale common and the baselines aligned.
    const pts = vertices.map((v) => `${v[0].toFixed(2)},${(H - v[1]).toFixed(2)}`).join(' ');
    const pad = Math.max(W, H) / 50;
    return (
      <svg viewBox={`${(-pad).toFixed(2)} ${(-pad).toFixed(2)} ${(W + pad * 2).toFixed(2)} ${(H + pad * 2).toFixed(2)}`}
        width={size} height={size} preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block' }} aria-hidden>
        <polygon points={pts} fill={P.accentSoft} stroke={P.accent}
          strokeWidth={Math.max(W, H) / 90} strokeLinejoin="round" />
      </svg>
    );
  }

  const xs = vertices.map((v) => v[0]);
  const ys = vertices.map((v) => v[1]);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const w = Math.max(1e-6, maxX - minX), h = Math.max(1e-6, maxY - minY);
  // Flip Y for SVG (Y-down) and translate to local origin.
  const pts = vertices.map((v) => `${(v[0] - minX).toFixed(2)},${(maxY - v[1]).toFixed(2)}`).join(' ');
  return (
    <svg viewBox={`-2 -2 ${(w + 4).toFixed(2)} ${(h + 4).toFixed(2)}`} width={size} height={size}
      preserveAspectRatio="xMidYMid meet" style={{ display: 'block' }} aria-hidden>
      <polygon points={pts} fill={P.accentSoft} stroke={P.accent} strokeWidth={Math.max(w, h) / 90} strokeLinejoin="round" />
    </svg>
  );
}
